import { useEffect, useMemo, useState } from "react";
import { MuiMaterials } from "../@index.js";
import { createApiEndpoint } from "../api/createApiEndpoint.js";
import * as global from "../data/global";
import { comparisonFieldSx } from "./windowChrome/actionButtons.js";
import { withScrollHintMenuProps } from "./windowChrome/ScrollHintPaper.js";

const FILTER_FIELDS = [
    ["all", "All fields"],
    ["assetTag", "Asset tag"],
    ["partNumber", "Part number"],
    ["serialNumber", "Serial number"],
    ["assetType", "Type"],
    ["manufacturer", "Manufacturer"],
    ["model", "Model"],
    ["area", "Area"],
    ["physicalLocation", "Location"],
    ["processFunction", "Function"],
    ["status", "Status"],
];

const HIDDEN_SCROLL_SX = {
    scrollbarWidth: "none",
    msOverflowStyle: "none",
    "&::-webkit-scrollbar": { display: "none", width: 0, height: 0 },
};

function fieldText(part, key) {
    return String(part?.[key] || "");
}

function themeColor(light, dark) {
    return global.gw.theme === "light" ? light : dark;
}

export default function CcrPartList({ siteGuid, onOpen }) {
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");
    const [field, setField] = useState("all");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const next = await createApiEndpoint().fetchCcrAssetList("direct", siteGuid);
            if (cancelled) return;
            if (next?.error) {
                setError(next.error);
                setParts([]);
            } else {
                setError("");
                setParts(Array.isArray(next?.assets) ? next.assets : []);
            }
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [siteGuid]);

    const filtered = useMemo(() => {
        const sorted = (Array.isArray(parts) ? parts : []).slice().sort((a, b) => (
            String(a?.assetTag || "").localeCompare(String(b?.assetTag || ""), undefined, { numeric: true, sensitivity: "base" })
        ));
        const needle = query.trim().toLowerCase();
        if (!needle) return sorted;
        return sorted.filter((part) => {
            const hay = field === "all"
                ? FILTER_FIELDS
                    .filter(([key]) => key !== "all")
                    .map(([key]) => fieldText(part, key))
                    .join(" ")
                : fieldText(part, field);
            return hay.toLowerCase().includes(needle);
        });
    }, [parts, query, field]);

    const fieldSx = comparisonFieldSx();
    const menuProps = withScrollHintMenuProps({
        PaperProps: {
            sx: {
                background: themeColor("#f8f8f8", "#313131"),
                color: themeColor("#333333", "#f4cc8e"),
                border: `1px solid ${themeColor("rgba(0, 0, 0, 0.15)", "rgba(244, 204, 142, 0.3)")}`,
            },
        },
    });

    return (
        <MuiMaterials.Box
            onClick={(e) => e.stopPropagation()}
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                px: { xs: "12px", sm: "14px" },
                pt: "10px",
                pb: "12px",
                boxSizing: "border-box",
            }}
        >
            <MuiMaterials.Box sx={{ display: "flex", gap: 1, width: "100%", alignItems: "center" }}>
                <MuiMaterials.Tooltip title="Choose which field the search looks at." arrow>
                <MuiMaterials.TextField
                    select
                    size="small"
                    value={field}
                    onChange={(e) => setField(e.target.value)}
                    SelectProps={{ MenuProps: menuProps }}
                    sx={{ ...fieldSx, minWidth: 150, flexShrink: 0 }}
                >
                    {FILTER_FIELDS.map(([value, label]) => (
                        <MuiMaterials.MenuItem key={value} value={value}>{label}</MuiMaterials.MenuItem>
                    ))}
                </MuiMaterials.TextField>
                </MuiMaterials.Tooltip>
                <MuiMaterials.Tooltip title="Filter this site's instruments by serial, part number, tag, or the field you picked." arrow>
                <MuiMaterials.TextField
                    size="small"
                    fullWidth
                    placeholder="Serial, part number, tag…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    sx={{
                        ...fieldSx,
                        "& .MuiInputBase-input::placeholder": {
                            color: themeColor("#666666", "#a8abb1"),
                            opacity: 1,
                        },
                    }}
                />
                </MuiMaterials.Tooltip>
            </MuiMaterials.Box>
            {loading && (
                <MuiMaterials.Typography sx={{ fontSize: 12, mt: 1, color: themeColor("#666666", "#a8abb1") }}>
                    Loading…
                </MuiMaterials.Typography>
            )}
            {!loading && error && (
                <MuiMaterials.Typography sx={{ fontSize: 12, mt: 1, color: "error.main" }}>{error}</MuiMaterials.Typography>
            )}
            {!loading && !error && (
                <MuiMaterials.Box
                    className="hide-scrollbar"
                    sx={{
                        mt: 0.5,
                        maxHeight: 280,
                        overflowY: "auto",
                        overflowX: "hidden",
                        ...HIDDEN_SCROLL_SX,
                    }}
                >
                    {filtered.length === 0 && (
                        <MuiMaterials.Typography sx={{ fontSize: 12, py: 1, color: themeColor("#666666", "#a8abb1") }}>
                            {parts.length === 0 ? "None on this site." : "Nothing matches."}
                        </MuiMaterials.Typography>
                    )}
                    {filtered.map((part) => (
                        <MuiMaterials.Tooltip key={part.assetTag || part.serialNumber || part.partNumber} title={`Open ${part.assetTag || "this instrument"}'s CCR.`} arrow>
                        <MuiMaterials.Box
                            role="button"
                            tabIndex={0}
                            onClick={() => onOpen(part.assetTag)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    onOpen(part.assetTag);
                                }
                            }}
                            sx={{
                                cursor: "pointer",
                                py: 0.75,
                                borderBottom: `1px solid ${themeColor("rgba(0, 0, 0, 0.06)", "rgba(255, 255, 255, 0.06)")}`,
                                "&:hover": {
                                    background: themeColor("rgba(0, 0, 0, 0.025)", "rgba(244, 204, 142, 0.05)"),
                                },
                            }}
                        >
                            <MuiMaterials.Typography sx={{
                                fontSize: { xs: 12, sm: 13, md: 14 },
                                fontWeight: 700,
                                letterSpacing: "0.5px",
                                lineHeight: 1.2,
                                color: themeColor("#2d2d2d", "#f4cc8e"),
                            }}>
                                {part.assetTag || "Untagged"}
                            </MuiMaterials.Typography>
                            <MuiMaterials.Typography sx={{
                                fontSize: { xs: 10, sm: 11, md: 12 },
                                color: themeColor("#2d2d2d", "#a8abb1"),
                                letterSpacing: "0.3px",
                            }}>
                                {[part.assetType, part.physicalLocation].filter(Boolean).join(" · ") || "Type and location to verify"}
                            </MuiMaterials.Typography>
                            <MuiMaterials.Typography sx={{
                                fontSize: { xs: 10, sm: 11, md: 12 },
                                color: themeColor("#2d2d2d", "#a8abb1"),
                                letterSpacing: "0.3px",
                            }}>
                                Part {part.partNumber || "—"} · Serial {part.serialNumber || "—"}
                            </MuiMaterials.Typography>
                        </MuiMaterials.Box>
                        </MuiMaterials.Tooltip>
                    ))}
                </MuiMaterials.Box>
            )}
        </MuiMaterials.Box>
    );
}
